import React, { useEffect, useState, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import * as ReactDOM from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Sparkles, Maximize2, Minimize2, X as XIcon } from 'lucide-react';
import ChatGptPromptModal from './ChatGptPromptModal';
import { useDebouncedCallback } from '../../lib/hooks/useDebounce';
import { usePermissions } from '../../lib/hooks/usePermissions';

interface DealServiceEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  serviceTitle?: string;
  translations?: any;
  /**
   * Riepilogo dei campi del servizio (nome, prezzo, durata, ecc.) mostrato a
   * sinistra della toolbar quando questa diventa sticky durante lo scroll.
   * Tieni testo breve — il container ha truncate.
   */
  summary?: React.ReactNode;
}

/**
 * Handle imperativo: permette al genitore di committare SINCRONAMENTE il testo
 * eventualmente ancora "in volo" nel timer di debounce, prima di leggere il
 * valore per il salvataggio. Ritorna l'HTML appena flushato (o null se non
 * c'era nulla in sospeso), così il chiamante può usarlo direttamente senza
 * dover attendere il giro di setState asincrono di React.
 */
export interface DealServiceEditorHandle {
  flush: () => string | null;
  /** Apre la descrizione nel foglio a tutta pagina (stessa istanza TipTap). */
  openFull: () => void;
}

export const DealServiceEditor = forwardRef<DealServiceEditorHandle, DealServiceEditorProps>(function DealServiceEditor(
  { content, onChange, placeholder = 'Write a description...', style, serviceTitle, translations, summary },
  ref,
) {
  const { canUseAITextGeneration } = usePermissions();
  const [showAiModal, setShowAiModal] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  // Contenuto "in volo" non ancora propagato al genitore. Tenuto in un ref (non
  // in stato) per evitare le stale-closure: gli handler di TipTap (onUpdate,
  // onBlur) vengono legati UNA SOLA VOLTA alla creazione dell'editor, quindi
  // devono leggere sempre l'ultimo valore via ref, non una copia congelata.
  const pendingContentRef = useRef<string | null>(null);
  // Stesso motivo per onChange: la prop arriva come arrow ricreata ad ogni
  // render del genitore — il ref garantisce che il flush chiami SEMPRE l'ultima
  // versione (con l'editingService aggiornato).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Drawer mode: l'editor viene portalled in un overlay sul fondo schermo a
  // ~55vh, utile per lavorare la descrizione su area piu' ampia senza
  // scrollare il pannello padre. Il TipTap editor viene riusato (stesso hook
  // singleton), quindi lo stato/cursore si preserva nel toggle.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const t = translations || {};
  
  // Porta del modale - da usare per renderizzare il modale direttamente nel body
  const portalRoot = typeof document !== 'undefined' ? document.body : null;
  
  // Debounce-based onChange: accumula i keystroke e propaga al genitore dopo
  // una breve pausa. 400ms (non più 3000ms): abbastanza per non spammare ad
  // ogni tasto, ma sotto la latenza umana di "smetto di scrivere → clicco
  // Salva", così la descrizione raggiunge `editingService` come fa il prezzo e
  // non viene persa dal salvataggio. Stabile (deps vuote): legge sempre l'ultimo
  // onChange via ref.
  const handleContentChange = useCallback((html: string) => {
    pendingContentRef.current = html;
    setIsUserTyping(true);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      onChangeRef.current(html);
      pendingContentRef.current = null;
      setIsUserTyping(false);
      timerRef.current = null;
    }, 400);
  }, []);

  // Forza il commit immediato del contenuto in sospeso. Stabile e ref-based:
  // funziona anche dentro l'onBlur di TipTap (legato una sola volta).
  const forceSave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingContentRef.current !== null) {
      const html = pendingContentRef.current;
      pendingContentRef.current = null;
      setIsUserTyping(false);
      onChangeRef.current(html);
      return html;
    }
    return null;
  }, []);

  // Espone `flush()` al genitore: committa sincronicamente l'eventuale testo in
  // volo e ne restituisce l'HTML, così il salvataggio dell'offerta non perde le
  // ultime parole digitate anche se l'utente clicca "Salva" entro il debounce.
  useImperativeHandle(
    ref,
    () => ({ flush: () => forceSave(), openFull: () => setDrawerOpen(true) }),
    [forceSave],
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-400 hover:text-blue-300 underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      // USA IL TIMER invece del debouncing - zero chiamate intermedie
      handleContentChange(editor.getHTML());
    },
    onFocus: () => {
      // Extra protezione: quando l'editor riceve focus, impedisci aggiornamenti esterni
      setIsUserTyping(true);
    },
    onBlur: () => {
      // Quando l'editor perde focus, forza il salvataggio immediato
      forceSave();
      // Poi permetti aggiornamenti esterni dopo un delay
      setTimeout(() => setIsUserTyping(false), 1000);
    }
  });

  // Update editor content when content prop changes (solo se l'utente non sta scrivendo)
  useEffect(() => {
    if (editor && content !== editor.getHTML() && !isUserTyping) {
      editor.commands.setContent(content);
    }
  }, [content, editor, isUserTyping]);

  // Cleanup timer all'unmount + flush del contenuto pending (es. quando il
  // pannello servizio si chiude o si cambia tab). Ref-based: niente stale
  // closure, committa sempre l'ultimo testo digitato.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (pendingContentRef.current !== null) {
        onChangeRef.current(pendingContentRef.current);
        pendingContentRef.current = null;
      }
    };
  }, []);

  // Toolbar resa funzione: renderizzata sia inline che dentro il drawer.
  // Inline: zero chrome — niente bordi, niente background pieno, solo backdrop
  // blur quando diventa sticky sopra il testo (Notion-like). Drawer: header
  // visibile con border-bottom per separazione dal contenuto.
  const renderToolbar = (variant: 'inline' | 'drawer') => (
    <div
      className={
        variant === 'inline'
          ? 'sticky top-0 z-10 px-2 py-1.5 flex items-center gap-2 bg-white dark:bg-gray-900 border-b border-black/[0.06] dark:border-white/[0.08] rounded-t-md'
          : 'flex-shrink-0 bg-white dark:bg-gray-900 border-b border-black/[0.06] dark:border-white/[0.08] px-3 py-2 flex items-center gap-2'
      }
    >
      {/* Riepilogo sticky a sinistra: visibile solo inline (nel drawer e' gia'
          ovvio quale servizio stai modificando). Truncate per non spingere
          giu' i bottoni quando il testo e' lungo. */}
      {variant === 'inline' && summary ? (
        <div className="min-w-0 flex-1 text-[11px] text-black/55 dark:text-white/55 truncate">
          {summary}
        </div>
      ) : variant === 'drawer' ? (
        <div className="min-w-0 flex-1 text-[13px] font-medium text-black dark:text-white truncate">
          {serviceTitle || 'Descrizione servizio'}
        </div>
      ) : (
        <div className="flex-1" />
      )}

      <div className="flex items-center gap-0.5 shrink-0">
        <ToolbarBtn active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()} title="Grassetto (Cmd/Ctrl+B)" Icon={Bold} />
        <ToolbarBtn active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()} title="Corsivo (Cmd/Ctrl+I)" Icon={Italic} />
        <ToolbarBtn
          active={editor?.isActive('link')}
          onClick={() => { const url = window.prompt('Enter URL'); if (url) editor?.chain().focus().setLink({ href: url }).run(); }}
          title="Inserisci link"
          Icon={LinkIcon}
        />
        <ToolbarBtn active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()} title="Elenco puntato" Icon={List} />
        <ToolbarBtn active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()} title="Elenco numerato" Icon={ListOrdered} />

        {/* Toggle drawer: passa tra inline e overlay 55vh */}
        <button
          type="button"
          onClick={() => setDrawerOpen(v => !v)}
          title={drawerOpen ? 'Chiudi vista estesa' : 'Apri vista estesa'}
          aria-label={drawerOpen ? 'Chiudi vista estesa' : 'Apri vista estesa'}
          className="p-1 rounded transition-colors text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-black dark:hover:text-white"
        >
          {drawerOpen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>

        {/* AI Button — chip discreto coerente col resto */}
        {canUseAITextGeneration && (
          <button
            type="button"
            onClick={() => setShowAiModal(true)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
            title={t.generateWithAI || 'Genera testo con IA'}
          >
            <Sparkles className="h-3 w-3" />
            <span className="hidden sm:inline-block">{t.aiAssistant || 'IA'}</span>
          </button>
        )}

        {variant === 'drawer' && (
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            title="Chiudi (Esc)"
            aria-label="Chiudi"
            className="p-1 rounded text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-black dark:hover:text-white transition-colors"
          >
            <XIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );

  // Close drawer with Esc.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="prose prose-sm max-w-none">
      {/* Inline Notion-style: niente bordi, niente card. Solo toolbar sticky
          che galleggia sopra il testo. L'editor cresce con il contenuto;
          lo scroll avviene nel container parent (il pannello servizio).
          Quando il drawer e' aperto, lasciamo solo un hint cliccabile. */}
      {!drawerOpen && (
        <div className="bg-white dark:bg-white/[0.02] border border-black/[0.06] dark:border-white/[0.08] rounded-md">
          {renderToolbar('inline')}
          {/* Override stili globali .ProseMirror (src/index.css:1572): in
              questo editor specifico non vogliamo bordo/sfondo/rounded che
              fanno apparire una card NESTED; il box bianco esterno fa gia'
              capire che e' un editor. I reset usano la sintassi arbitraria
              di Tailwind. */}
          <EditorContent
            editor={editor}
            className="min-h-[200px] px-3 pb-3 focus:outline-none text-[14px] leading-relaxed text-black dark:text-white [&_.ProseMirror]:!border-0 [&_.ProseMirror]:!bg-transparent [&_.ProseMirror]:!p-0 [&_.ProseMirror]:!rounded-none [&_.ProseMirror]:!shadow-none [&_.ProseMirror]:!backdrop-blur-none [&_.ProseMirror]:!min-h-[180px]"
            style={style}
          />
        </div>
      )}
      {drawerOpen && (
        <button
          type="button"
          onClick={() => setDrawerOpen(false)}
          className="w-full py-3 flex items-center justify-center gap-2 text-[12px] text-black/45 dark:text-white/45 hover:text-black/70 dark:hover:text-white/70 transition-colors"
        >
          <Minimize2 className="h-3.5 w-3.5" />
          <span>Descrizione in modifica nel drawer — clicca per ripristinare qui</span>
        </button>
      )}

      {/* Foglio a tutta pagina: pagina bianca centrata, testo in colonna
          stretta, come si scrive un documento. Il TipTap editor viene montato
          qui — l'istanza e' la stessa (useEditor), quindi contenuto, cronologia
          e cursore si preservano passando da inline a foglio e ritorno. */}
      {drawerOpen && portalRoot && ReactDOM.createPortal(
        <div
          // z sopra il modale dell'offerta, che gira a 20000: con z-50 il
          // foglio finiva dietro e si vedeva solo il bordo.
          className="fixed inset-0 z-[20050] bg-black/45 flex items-start justify-center px-4 py-[5vh] animate-[fadeIn_0.15s_ease-out]"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false); }}
        >
          {/* role="dialog" sta sul foglio, non sullo sfondo: una regola globale
              (html:not(.dark) [role="dialog"]) forza il bianco, e sull'overlay
              cancellava il fondo scuro. */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Descrizione a tutta pagina"
            className="w-full max-w-[820px] h-[85vh] max-h-[900px] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden"
          >
            {renderToolbar('drawer')}
            <div className="flex-1 overflow-y-auto px-6 py-10">
              <EditorContent
                editor={editor}
                className="max-w-[640px] mx-auto focus:outline-none text-[15px] leading-[1.7] text-black dark:text-white [&_.ProseMirror]:!border-0 [&_.ProseMirror]:!bg-transparent [&_.ProseMirror]:!p-0 [&_.ProseMirror]:!rounded-none [&_.ProseMirror]:!shadow-none [&_.ProseMirror]:!backdrop-blur-none [&_.ProseMirror]:!min-h-[400px]"
                style={style}
              />
            </div>
            <div className="flex-shrink-0 px-6 py-3 bg-black/[0.02] dark:bg-white/[0.03] border-t border-black/[0.06] dark:border-white/10">
              <p className="max-w-[640px] mx-auto text-[12px] text-black/40 dark:text-white/40">
                Le modifiche valgono per questa offerta. Chiudendo, il testo torna nella riga servizio.
              </p>
            </div>
          </div>
        </div>,
        portalRoot
      )}

      {/* AI Modal - renderizzato nel portale per evitare problemi di z-index e overflow */}
      {portalRoot && showAiModal && ReactDOM.createPortal(
        <ChatGptPromptModal
          isOpen={true}
          onClose={() => setShowAiModal(false)}
          onTextGenerated={(generatedText) => {
            if (editor) {
              // Rimuove i delimitatori di codice ```html e ``` che l'AI potrebbe includere
              let cleanedText = generatedText;
              
              // Rimuove ```html all'inizio
              cleanedText = cleanedText.replace(/^```html\s*/i, '');
              // Rimuove ``` alla fine
              cleanedText = cleanedText.replace(/\s*```$/i, '');
              // Rimuove anche eventuale ```html senza specifiche di linguaggio
              cleanedText = cleanedText.replace(/^```\s*/i, '');
              
              editor.commands.setContent(cleanedText);
              onChange(cleanedText);
            }
          }}
          currentText={content}
          serviceTitle={serviceTitle}
          translations={t}
        />,
        portalRoot
      )}
    </div>
  );
});

interface ToolbarBtnProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  Icon: React.ComponentType<{ className?: string }>;
}

function ToolbarBtn({ active, onClick, title, Icon }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-black/[0.08] dark:bg-white/[0.1] text-black dark:text-white'
          : 'text-black/55 dark:text-white/55 hover:bg-black/[0.04] dark:hover:bg-white/[0.06] hover:text-black dark:hover:text-white'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}